// src/Pages/Admin/Components/Contratos/contratoPdf.js
// Fuente única de verdad del contrato en PDF.
//
// Antes de este módulo el `generarContrato` vivía adentro de `Contratos.js`, así
// que la ficha del cliente no tenía forma de generar un contrato sin copiar las
// 200 líneas del documento. Ahora tanto la pestaña "Contratos" como la ficha del
// cliente arman el mismo PDF desde acá.
import jsPDF from "jspdf";

export const ESTADOS = ["pendiente", "confirmada", "pagada", "completada", "cancelada"];

export const ESTADO_LABELS = {
  pendiente: "Pendiente",
  confirmada: "Confirmada",
  pagada: "Pagada",
  completada: "Completada",
  cancelada: "Cancelada",
};

export const CONTRATISTA = {
  nombre: "Costilla María Soledad",
  dni: "33.966.161",
  direccion: "Bolivia 83, Yerba Buena, Tucumán",
  localidad: "San Miguel de Tucumán",
};

export const CAMPOS_CLIENTE = ["nombre", "email", "dni", "telefono", "direccion"];

export const ETIQUETAS_CLIENTE = {
  nombre: "Nombre y apellido",
  email: "Email",
  dni: "DNI",
  telefono: "Teléfono",
  direccion: "Domicilio",
};

// Placeholder en el PDF cuando un dato no está cargado
export const HUECO = "____________________";

const MESES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

/**
 * "2025-10-15" -> "15 de octubre de 2025".
 *
 * Un `date` de Postgres ("2025-10-15") pasa por `new Date()` como UTC midnight,
 * así que en Argentina `getDate()` devolvería 14. Por eso las fechas sin hora se
 * leen a mano del string.
 */
export const fechaLarga = (valor) => {
  if (!valor) return null;

  const soloFecha = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(valor).slice(0, 10));
  if (soloFecha) {
    const [, anio, mes, dia] = soloFecha;
    return `${Number(dia)} de ${MESES[Number(mes) - 1]} de ${anio}`;
  }

  const d = new Date(valor);
  if (Number.isNaN(d.getTime())) return null;
  return `${d.getDate()} de ${MESES[d.getMonth()]} de ${d.getFullYear()}`;
};

/** El servicio contratado de la orden (resuelto por el caller, sin embed). */
export const obtenerServicio = (orden) => orden?.servicios || null;

/** Datos del cliente tal como los necesita el contrato. */
export const datosCliente = (orden) => {
  const u = orden?.usuarios;
  return {
    nombre: (u?.nombre || "").trim(),
    email: (u?.email || "").trim(),
    dni: (u?.dni || "").trim(),
    telefono: (u?.telefono || "").trim(),
    direccion: (u?.direccion || "").trim(),
  };
};

/**
 * Lista de campos que le faltan al contrato. Se muestra en la tarjeta, en la
 * ficha del cliente y en el PDF para que el admin sepa exactamente qué pedirle
 * al cliente.
 *
 * @param {object} orden
 * @param {{exigirEvento?: boolean, evento?: {fecha?: string, lugar?: string}}} opciones
 *   `exigirEvento` agrega fecha y lugar a la lista. La ficha del cliente lo pide
 *   porque ahí el admin ve las reservas del cliente y puede completarlas de una;
 *   en la lista de órdenes quedan como líneas en blanco para llenar a mano.
 */
export const datosFaltantes = (orden, { exigirEvento = false, evento } = {}) => {
  const faltan = [];
  const u = orden?.usuarios;

  if (!obtenerServicio(orden)?.nombre) faltan.push("Servicio contratado");
  if (!(u?.nombre || "").trim()) faltan.push("Nombre y apellido del cliente");
  if (!(u?.dni || "").trim()) faltan.push("DNI del cliente");
  if (!(u?.telefono || "").trim()) faltan.push("Teléfono del cliente");
  if (!(u?.direccion || "").trim()) faltan.push("Domicilio del cliente");
  if (!orden?.metodoPago) faltan.push("Método de pago");
  if (!orden?.cantidad) faltan.push("Cantidad");

  if (exigirEvento) {
    if (!fechaLarga(evento?.fecha)) faltan.push("Fecha del evento");
    if (!(evento?.lugar || "").trim()) faltan.push("Lugar del evento");
  }

  return faltan;
};

/**
 * Precio real de la orden: si hay cantidad y precio unitario los multiplicamos,
 * si no caemos al monto total. Nunca usamos el precio de catálogo del servicio
 * porque puede haber cambiado desde que el cliente compró.
 */
export const obtenerMonto = (orden) => {
  const unitario = Number(orden?.precio_unitario);
  const cantidad = Number(orden?.cantidad);
  if (Number.isFinite(unitario) && unitario > 0 && Number.isFinite(cantidad) && cantidad > 0) {
    return unitario * cantidad;
  }
  const total = Number(orden?.montoTotal);
  return Number.isFinite(total) ? total : 0;
};

/**
 * Arma y descarga el PDF del contrato.
 *
 * @param {object} orden - orden con `usuarios` y `servicios` ya resueltos.
 * @param {object} [opciones]
 * @param {{fecha?: string, hora?: string, lugar?: string}} [opciones.evento]
 *   Datos del evento para la cláusula PRIMERA. La ficha del cliente los saca de
 *   la reserva, así el PDF no sale con huecos que el admin ya puede completar.
 * @returns {{filename: string, faltan: string[]}}
 */
export const generarContratoPDF = (orden, { evento } = {}) => {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const m = 40; // margen
  let y = 60;
  const lineHeight = 18;

  const servicio = obtenerServicio(orden);
  const cliente = datosCliente(orden);
  const faltan = datosFaltantes(orden, { exigirEvento: Boolean(evento), evento });

  // Datos faltantes se imprimen como línea en blanco para completar a mano
  const clientName = cliente.nombre || cliente.email || HUECO;
  const clientDNI = cliente.dni ? `DNI ${cliente.dni}` : `DNI ${HUECO}`;
  const servicioNombre = servicio?.nombre || HUECO;
  const servicioDesc = servicio?.descripcion || "";

  const totalPrice = obtenerMonto(orden);
  const priceStr = `USD ${totalPrice.toFixed(2)}`;

  const payMethod = orden.metodoPago || HUECO;
  const eventDate = fechaLarga(evento?.fecha) || `____ de ______ de ${new Date().getFullYear()}`;
  const eventTime = (evento?.hora || "").trim();
  const eventPlace = (evento?.lugar || "").trim() || "____________________________";

  const addMultiLineText = (text, x, yPos, maxWidth, lh) => {
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line) => {
      if (yPos > 750) {
        doc.addPage();
        yPos = 60;
      }
      doc.text(line, x, yPos);
      yPos += lh;
    });
    return yPos;
  };

  const addTitle = (title, x, yPos, lh) => {
    doc.setFont(undefined, "bold");
    doc.text(title, x, yPos);
    doc.setFont(undefined, "normal");
    return yPos + lh;
  };

  // ---------------- AVISO DE DATOS PENDIENTES ----------------
  if (faltan.length > 0) {
    doc.setFillColor(255, 247, 237);
    doc.setDrawColor(234, 179, 8);
    doc.setLineWidth(1);
    doc.roundedRect(m, y - 22, 520, 34 + faltan.length * 14, 6, 6, "FD");

    doc.setTextColor(120, 53, 15);
    doc.setFontSize(9);
    doc.setFont(undefined, "bold");
    doc.text("DOCUMENTO INCOMPLETO - DATOS PENDIENTES DE COMPLETAR CON EL CLIENTE:", m + 12, y);
    doc.setFont(undefined, "normal");
    y += 15;
    doc.text(faltan.join(" · "), m + 12, y, { maxWidth: 496 });
    y += 26;
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(12);
  }

  // ---------------- TÍTULO ----------------
  doc.setFontSize(18);
  doc.setFont(undefined, "bold");
  doc.text(`CONTRATO DE OBRAS Y SERVICIOS - ${servicioNombre}`, m, y, {
    maxWidth: 620,
    align: "justify",
  });
  y += 40;

  doc.setFontSize(12);
  doc.setFont(undefined, "normal");
  const intro =
    `Entre ${CONTRATISTA.nombre}, DNI ${CONTRATISTA.dni}, domiciliado en ${CONTRATISTA.direccion}, en adelante "El Contratista", ` +
    `y ${clientName} (${clientDNI}), en adelante "El Cliente", convienen celebrar el presente contrato de obras y servicios conforme a las cláusulas que se detallan a continuación:`;
  y = addMultiLineText(intro, m, y, 520, lineHeight);
  y += 30;

  // ---------------- DATOS DE CONTACTO DEL CLIENTE ----------------
  y = addTitle("DATOS DEL CLIENTE:", m, y, lineHeight);
  y = addMultiLineText(
    [
      `Nombre: ${cliente.nombre || HUECO}`,
      `DNI: ${cliente.dni || HUECO}`,
      `Teléfono: ${cliente.telefono || HUECO}`,
      `Domicilio: ${cliente.direccion || HUECO}`,
      `Email: ${cliente.email || HUECO}`,
    ].join("\n"),
    m + 10,
    y,
    500,
    lineHeight
  );
  y += 20;

  // ---------------- CLÁUSULAS ----------------
  const clauses = [
    {
      title: "PRIMERA. DETALLE DEL EVENTO, LUGAR Y FECHA:",
      text:
        `Servicio: ${servicioNombre}\nFecha del evento: ${eventDate}\n` +
        (eventTime ? `Horario: ${eventTime}\n` : "") +
        `Lugar: ${eventPlace}`,
    },
    {
      title: "SEGUNDA. PRECIO:",
      text: `${priceStr}, pagaderos mediante ${payMethod}. Se abonará seña de _______ y saldo restante de _______ a fecha ____/____/____.`,
    },
    {
      title: "TERCERA. EL PRECIO INCLUYE:",
      text: servicioDesc || "Detalles del servicio contratado.",
    },
    {
      title: "CUARTA. RESERVACIÓN:",
      text: "La firma del contrato y anticipo es requerido para reservar el evento.",
    },
    {
      title: "QUINTA. CONSULTA PREVIA:",
      text: "Las partes acuerdan reunirse antes del evento para tratar la logística de la toma fotográfica.",
    },
    {
      title: "SEXTA. COOPERACIÓN:",
      text: "El Cliente se compromete a proporcionar cooperación y comunicación para un resultado óptimo.",
    },
    {
      title: "SÉPTIMA. TIEMPO DEDICADO:",
      text: "El/los Fotógrafo/s asistirá/n en la fecha y horas pactadas, cubriendo la esencia del evento.",
    },
    {
      title: "OCTAVA. REGLAS DE CASA:",
      text: "El/los Fotógrafo/s estará/n limitado/s a las reglas del lugar del evento, Iglesia o recepción.",
    },
    {
      title: "NOVENA. LÍMITE DE RESPONSABILIDAD:",
      text: "Si por fuerza mayor no pudiera/n cumplir, se buscará reemplazo profesional y se informará al Cliente.",
    },
    {
      title: "DÉCIMA. ANTICIPO DE SEGURIDAD:",
      text: "Si se cancela el evento, el anticipo no será devuelto. Cambio de fecha sujeto a disponibilidad.",
    },
    {
      title: "DÉCIMA PRIMERA. TIEMPO DE ENTREGA:",
      text: "Entrega del trabajo aproximadamente 60 días hábiles tras el evento. Entrega anticipada con cargo extra.",
    },
    {
      title: "DÉCIMA SEGUNDA. LEY APLICABLE:",
      text: "Se rige por los artículos 1.251 a 1.277 de la Ley 26.994 (Código Civil y Comercial).",
    },
    {
      title: "DÉCIMA TERCERA. TRIBUNALES COMPETENTES:",
      text: `Las partes se someten a la jurisdicción de los tribunales de ${CONTRATISTA.localidad}.`,
    },
  ];

  clauses.forEach((cl) => {
    y = addTitle(cl.title, m, y, lineHeight);
    y = addMultiLineText(cl.text, m + 10, y, 500, lineHeight);
    y += 20;
  });

  // ---------------- NOTAS ----------------
  const notes = [
    "La comida de los participantes debe estar cubierta.",
    "En caso de disturbios, el equipo se retira sin responsabilidad.",
    "Viáticos no incluidos, se cotizan el mismo día.",
    "Fotos podrían usarse con fines publicitarios, si no se abonará extra.",
    "Avisar al personal del salón de no utilizar máquina de humo o láser.",
    "Trabajo debe ser abonado siete días antes del evento, de no ser así no se presenta el equipo.",
    "El Cliente debe resguardar la información obtenida; el fotógrafo borra todo el material tras la entrega.",
  ];

  y = addTitle("Notas:", m, y, lineHeight);
  notes.forEach((note) => {
    y = addMultiLineText(`- ${note}`, m + 10, y, 500, lineHeight);
    y += 5;
  });

  // ---------------- FIRMA ----------------
  const dateStr = new Date(orden.fecha_creacion || Date.now()).toLocaleDateString("es-AR");
  y += 30;
  y = addMultiLineText(
    `En ${CONTRATISTA.localidad}, a los ${dateStr}.\nSe firman dos ejemplares del mismo tenor y a un solo efecto.`,
    m,
    y,
    520,
    lineHeight
  );

  // ---------------- BARRAS DE FIRMA ----------------
  y += 60;
  doc.line(m, y, m + 220, y);
  doc.text("Firma del Cliente", m, y + 15);
  doc.line(m + 300, y, m + 520, y);
  doc.text("Firma del Contratista", m + 300, y + 15);
  y += 40;

  const filename = `contrato_orden_${orden.id}.pdf`;
  doc.save(filename);

  return { filename, faltan };
};

/**
 * Arma el texto para pedirle al cliente los datos que faltan.
 *
 * @param {object} p
 * @param {string} p.nombre
 * @param {string|number} [p.ordenId]
 * @param {string} [p.servicio]
 * @param {string[]} p.faltan
 */
export const mensajeSolicitarDatos = ({ nombre, ordenId, servicio, faltan }) => {
  const referencia = ordenId ? ` (*Orden #${ordenId}*)` : "";
  const que = servicio ? ` por tu servicio de *${servicio}*` : "";

  return (
    `¡Hola ${nombre || "!"}! Te escribo de parte de *SC Group*${que}${referencia}.\n\n` +
    (faltan.length > 0
      ? `Para completar tu contrato necesito que me confirmes los siguientes datos:\n\n` +
        faltan
          .map((f) => `• ${f.replace(" del cliente", "").replace("Metodo de pago", "Método de pago")}`)
          .join("\n") +
        `\n\n`
      : "") +
    `¿Me los podés pasar por acá? ¡Gracias!`
  );
};

/**
 * Abre WhatsApp o mail con un mensaje. Devuelve el canal usado para que la UI
 * pueda avisarle al admin que no hay con qué Contactar.
 *
 * @returns {'whatsapp' | 'email' | null}
 */
export const abrirContacto = ({ telefono, email, asunto, mensaje }) => {
  if (telefono) {
    const num = telefono.replace(/[^0-9]/g, "");
    if (num) {
      window.open(`https://wa.me/${num}?text=${encodeURIComponent(mensaje)}`, "_blank");
      return "whatsapp";
    }
  }

  if (email) {
    window.open(
      `mailto:${email}?subject=${encodeURIComponent(asunto || "Datos para tu contrato")}&body=${encodeURIComponent(mensaje)}`,
      "_blank"
    );
    return "email";
  }

  return null;
};

/**
 * Atajo de `mensajeSolicitarDatos` + `abrirContacto` para una orden.
 * @returns {'whatsapp' | 'email' | null}
 */
export const contactarClientePorDatos = (orden, { faltan, evento } = {}) => {
  const { telefono, email, nombre } = datosCliente(orden);
  const faltanLista = faltan || datosFaltantes(orden, { exigirEvento: Boolean(evento), evento });

  const mensaje = mensajeSolicitarDatos({
    nombre,
    ordenId: orden?.id,
    servicio: obtenerServicio(orden)?.nombre,
    faltan: faltanLista,
  });

  return abrirContacto({
    telefono,
    email,
    asunto: `Datos para tu contrato${orden?.id ? ` - Orden #${orden.id}` : ""}`,
    mensaje,
  });
};
